import { CheckCircle2, Mail, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/format";

/**
 * What is still missing before customer emails can go out.
 *
 * When the hold-up is domain verification, the notice also shows Resend's own
 * last answer and when we asked, with a button to ask again. Most API keys are
 * restricted to sending only, so the app cannot look the domain up - it has to
 * ask by trying, and the team should never be left staring at a warning that
 * might already be out of date.
 */
export const EmailSetupNotice = ({
  message,
  requiredEnv,
  domainPending = false,
  lastChecked = null,
  lastAnswer = "",
  busy = false,
  onRecheck = null,
}) => (
  <div
    data-testid="email-panel-setup"
    className="mt-4 rounded-lg border px-4 py-3.5"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
      color: "var(--tt-warn-text)",
    }}
  >
    <p className="flex items-start gap-2 text-[13px] leading-relaxed">
      <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </p>

    {domainPending && onRecheck ? (
      <div
        className="mt-3 rounded-md border px-3 py-2.5"
        style={{ borderColor: "var(--tt-warn-border)", backgroundColor: "rgba(255,255,255,0.45)" }}
        data-testid="email-panel-domain-check"
      >
        <p className="text-[12px] font-semibold uppercase tracking-wide opacity-80">
          Resend&apos;s answer
        </p>
        {lastAnswer ? (
          <p className="mt-1 text-[13px] leading-relaxed" data-testid="email-panel-domain-answer">
            {lastAnswer}
          </p>
        ) : null}
        <p className="mt-1.5 text-[12px] opacity-80" data-testid="email-panel-domain-checked-at">
          {lastChecked
            ? `Last checked ${formatDateTime(lastChecked)}.`
            : "Not checked yet since the backend started."}
        </p>
        <p className="mt-1.5 text-[12px] leading-relaxed opacity-80">
          Already published the DNS records? Check again — nothing is sent to anybody, we
          only ask Resend whether it will accept your domain.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2.5 bg-white"
          disabled={busy}
          onClick={onRecheck}
          data-testid="email-panel-domain-recheck-button"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
          {busy ? "Asking Resend…" : "Check the domain again"}
        </Button>
      </div>
    ) : null}

    <ul className="mt-3 space-y-1.5">
      {(requiredEnv || []).map((item) => (
        <li key={item.name} className="flex items-start gap-2 text-[13px]">
          {item.set ? (
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
          )}
          <span>
            <span className="font-amount font-bold">{item.name}</span>
            <span className="opacity-80"> — {item.help}</span>
          </span>
        </li>
      ))}
    </ul>
    <p className="mt-3 text-[12px] leading-relaxed opacity-90">
      Add them to <span className="font-amount font-bold">backend/.env</span> and restart the
      backend. Quotes keep working normally in the meantime.
    </p>
  </div>
);
